import { useFormikContext } from "formik";
import { ChangeEventHandler, useState } from "react";

import { Checkbox, FormControlLabel, makeStyles } from "@material-ui/core";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { FormikSelect } from "src/components/form/Select";
import { ApprovalRequirement } from "src/graphql/generated/globalTypes";
import { useFeatureFlag } from "src/util/featureFlags";

import FormikSwitchField from "./form/FormikSwitchField";

export const CONFIRMATION_TEXT = `
    WARNING: “Clicking” (manual pre-approval of all or certain types of
    phone numbers before dialing them) may be required depending on the
    purpose of your calling and relevant law and regulations. By
    confirming this dialog, you the Customer accept full legal
    responsibility for any call made without the use of the “clicker,”
    and that your organization's use of the Dialer to place calls
    without a “click” (pre-approval) complies with applicable law. In
    addition, you acknowledge and agree that removing the “click”
    requirement for calls may increase the risk of consumer liability,
    statutory violations, and other liability and you agree to defend,
    indemnify, and hold harmless Scale to Win from any and all liability
    that might arise from the use of the Dialer without a “click”
    requirement.`;

const APPROVER_ROLES = [
  "Candidate",
  "Campaign Manager",
  "Department Director",
  "Senior Advisor",
  "Executive Director",
  "Chair",
  "Senior Manager",
  "Treasurer",
  "Legal Counsel"
];

export function ApprovalFields(): null | JSX.Element {
  const disableClickerFeatureFlag = useFeatureFlag("disableClicker");

  if (!disableClickerFeatureFlag) return null;
  return (
    <>
      <ApprovalRequirementField />
      <VoicemailAllowedField />
    </>
  );
}

function ApprovalRequirementField() {
  const [showDialog, setShowDialog] = useState(false);
  const [authorized, setAuthorization] = useState(false);
  const { setFieldValue, isSubmitting, values } = useFormikContext<any>();
  const classes = useStyles();

  const handleChange: ChangeEventHandler<{ value: string }> = event => {
    if (event.target.value === ApprovalRequirement.NONE) {
      setShowDialog(true);
      event.preventDefault();
    } else {
      setFieldValue("approval_requirement", event.target.value);
    }
  };

  const handleConfirm = () => {
    setFieldValue("approval_requirement", ApprovalRequirement.NONE);
  };

  const helpTextAndLinkToSupportArticle = (
    <>
      Choose whether to require the “clicking” (manual pre-approval) of some,
      all, or none of the phone numbers contained in the contact lists you
      upload to calling campaigns in this dialer organization. Relevant state
      and federal law obligations may apply. Read more about the clicker
      function{" "}
      <a href="https://support.scaletowin.com/settings" target="_blank">
        here
      </a>
      .
    </>
  );

  return (
    <>
      <FormikSelect
        label="Clicking requirement"
        name="approval_requirement"
        fullWidth
        disabled={isSubmitting}
        onChange={handleChange}
        helperText={helpTextAndLinkToSupportArticle}
      >
        <MenuItem value={ApprovalRequirement.ALL}>
          Require clicking for all types of phone numbers
        </MenuItem>

        <MenuItem value={ApprovalRequirement.NON_LANDLINE}>
          Require clicking only for cell and VOIP phone numbers, not landlines
        </MenuItem>
        <MenuItem value={ApprovalRequirement.NONE}>
          Do not require clicking for any phone numbers
        </MenuItem>
      </FormikSelect>

      <Dialog
        open={showDialog}
        aria-labelledby="alert-dialog-title"
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Accept obligations and liability for use of Dialer without “clicker”
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">{CONFIRMATION_TEXT}</Alert>
          <FormControlLabel
            className={classes.checkboxContainer}
            control={
              <Checkbox
                checked={authorized}
                onChange={(_, checked) => setAuthorization(checked)}
              />
            }
            label="I represent and warrant that I am duly authorized by the Customer to
            enter into this agreement."
          />
          <FormikSelect
            label="Position you hold at your organization"
            name="approverRole"
            fullWidth
            disabled={isSubmitting}
          >
            {APPROVER_ROLES.map(role => (
              <MenuItem key={`role-menu-item-${role}`} value={role}>
                {role}
              </MenuItem>
            ))}
          </FormikSelect>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!values.approverRole || !authorized}
            onClick={() => {
              setShowDialog(false);
              handleConfirm();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  switchContainer: {
    marginBottom: theme.spacing(4)
  },
  checkboxContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

function VoicemailAllowedField() {
  const classes = useStyles();
  return (
    <div className={classes.switchContainer}>
      <FormikSwitchField
        label="Allow pre-recorded voicemail to be left on landlines"
        name="voicemail_allowed"
      />
    </div>
  );
}
